import downloadBlob from './downloadBlob';
import errorActions from 'app/client/store/actions/errorActions';

/**
 * Fetch a file as a download instead of navigating to it, which would put the url
 * in the browser history. Answers with the JSON:API errors on a failure, reported
 * through `dispatch` as the fetcher would; `name` is used when the response has none.
 */
export default async (url, name, dispatch = null) => {
  const failure = (status, detail = null) => {
    const action = status === 0 ? errorActions.network : errorActions[status];

    if (dispatch && action) {
      action(dispatch);
    }

    return { errors: [{ status: String(status), detail }] };
  };

  // A refused request rejects rather than answering, and callers read the errors
  // off the return value.
  const response = await fetch(url).catch(() => null);

  if (!response) {
    return failure(0);
  }

  // A route that guards its files answers an expired session with a redirect to
  // the login page, which fetch follows into a 200 of HTML.
  if (response.redirected && new URL(response.url).pathname === '/user/login') {
    return failure(401);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);

    // The storage endpoints answer with a `message` and their middleware with a
    // `msg`, where the error components expect the JSON:API errors.
    const failed = failure(response.status, body?.message ?? body?.msg);

    return body?.errors ? body : failed;
  }

  const [, fileName] =
    response.headers.get('content-disposition')?.match(/filename="?([^";]+)"?/) ?? [];

  // The body streams after the headers, so the connection can still drop here.
  const blob = await response.blob().catch(() => null);

  if (!blob) {
    return failure(0);
  }

  downloadBlob(blob, fileName ?? name);

  return null;
};
